import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, AlertTriangle, Send } from "lucide-react";
import SpeakButton from "@/components/SpeakButton";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

const levelStyle = (lvl) => {
  if (lvl === "emergency") return "bg-rose-50 text-rose-700 border-rose-200";
  if (lvl === "urgent") return "bg-amber-50 text-amber-700 border-amber-200";
  if (lvl === "routine") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

export default function TriageDrawer({ open, onOpenChange, clinicId, sessionId, onConvertToNote }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!sessionId) return;
    const r = await api.get(`/clinics/${clinicId}/triage-sessions/${sessionId}`);
    setData(r.data);
  };

  useEffect(() => {
    if (open && sessionId) {
      setData(null);
      setReply("");
      load();
    }
    // eslint-disable-next-line
  }, [open, sessionId, clinicId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [data?.messages?.length]);

  const convert = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clinics/${clinicId}/triage-sessions/${sessionId}/convert-to-note`);
      onConvertToNote?.(r.data);
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await api.post(`/clinics/${clinicId}/triage-sessions/${sessionId}/reply`, { text });
      setReply("");
      toast.success(`Sent${r.data.whatsapp?.simulated ? " (WhatsApp simulated — see Twilio setup note)" : " via WhatsApp"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setSending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[560px] p-0 flex flex-col" data-testid="triage-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading text-xl">{t("triage_full_chat")}</SheetTitle>
            {data?.session?.triage_level && (
              <span className={`text-xs font-bold uppercase rounded-full px-2.5 py-1 border ${levelStyle(data.session.triage_level)}`}>
                {data.session.triage_level}
              </span>
            )}
          </div>
          <SheetDescription className="text-slate-500">
            {data?.session?.patient_name} · {data?.session?.patient_phone || t("triage_no_phone")}
          </SheetDescription>
        </SheetHeader>

        {data?.session?.summary && (
          <div className="px-6 py-3 bg-[#e6f0ff] border-b border-blue-100">
            <div className="tiny-label text-[#004a99] flex items-center justify-between">
              <span>{t("triage_summary")}</span>
              <SpeakButton text={data.session.summary} lang={lang} testId="speak-summary" />
            </div>
            <div className="text-sm text-slate-800 mt-1">{data.session.summary}</div>
            {data.session.recommendation && (
              <div className="mt-2 text-xs text-slate-600 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[#f37021] mt-0.5" />
                {data.session.recommendation}
              </div>
            )}
          </div>
        )}

        <div ref={scrollRef} className="flex-1 wa-bg overflow-y-auto p-4 space-y-2">
          {!data && <div className="text-center text-slate-500 py-12">Loading conversation…</div>}
          {data?.messages?.length === 0 && <div className="text-center text-slate-500 py-12">No messages yet.</div>}
          {data?.messages?.map((m, i) => (
            <div key={m.id} className={`flex ${m.sender === "patient" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm fade-up ${
                m.sender === "patient" ? "bg-[#dcf8c6] rounded-tr-none" : "bg-white rounded-tl-none"}`}
                data-testid={`drawer-msg-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="flex-1">{m.text}</span>
                  <SpeakButton text={m.text} lang={lang} testId={`speak-msg-${i}`} />
                </div>
                <div className="text-[10px] text-slate-400 mt-1 text-right">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Reply composer */}
        <div className="bg-[#f0f0f0] p-2 flex items-center gap-2 border-t border-slate-200">
          <Input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !sending && sendReply()}
            placeholder={t("triage_reply_placeholder")}
            className="bg-white rounded-full px-4"
            data-testid="drawer-reply-input"
          />
          <Button onClick={sendReply} disabled={sending || !reply.trim()} className="rounded-full h-10 w-10 p-0 bg-[#25D366] hover:bg-[#1ea655]" data-testid="drawer-reply-btn">
            <Send className="h-4 w-4 text-white" />
          </Button>
        </div>

        <div className="p-3 border-t border-slate-200 bg-white flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="drawer-close-btn">{t("btn_close")}</Button>
          <Button disabled={busy || !data} onClick={convert} className="flex-1 bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="drawer-convert-btn">
            <FileText className="h-4 w-4 mr-2" /> {busy ? "Loading…" : t("btn_convert_note")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
